import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, Card, Col, Input, Row, Spin, Tag, Typography } from 'antd';
import { PrinterOutlined, SearchOutlined } from '@ant-design/icons';

import { getApplicantByApplicationNumber, printAdmissionLetter } from './request';
import { formatFullDate } from '../../../utils/formatDate';
import userStyles from '../../userManagement/styles.module.scss';

const { Title, Text } = Typography;

const parsePrintErrorMessage = async (error: any): Promise<string> => {
  const fallback = 'Unable to generate the admission letter. Please try again.';
  try {
    const blob: Blob = error?.response?.data;
    if (blob instanceof Blob) {
      const text = await blob.text();
      const json = JSON.parse(text);
      return json?.message || fallback;
    }
  } catch {
    // fall through to fallback message below
  }
  return error?.response?.data?.message || fallback;
};

const InfoItem = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Col xs={24} sm={12} lg={8} className={userStyles.infoItem}>
    <Text className={userStyles.infoLabel}>{label}</Text>
    <div className={userStyles.infoValue}>{value || 'N/A'}</div>
  </Col>
);

const AdmissionLetter = () => {
  const { notification } = App.useApp();

  const [searchTerm, setSearchTerm] = useState('');
  const [applicationNumber, setApplicationNumber] = useState('');

  const {
    data: applicant,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ['get-applicant-by-application-number', applicationNumber],
    queryFn: () => getApplicantByApplicationNumber(applicationNumber),
    enabled: !!applicationNumber,
  });

  const { mutate: print, isPending: isPrinting } = useMutation({
    mutationFn: printAdmissionLetter,
    onSuccess: (blob) => {
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    },
    onError: async (err: any) => {
      notification.error({
        message: 'Print Error',
        description: await parsePrintErrorMessage(err),
      });
    },
  });

  const handleSearch = () => {
    const trimmed = searchTerm.trim();
    if (!trimmed) return;
    setApplicationNumber(trimmed);
  };

  return (
    <Card>
      <Title level={4} style={{ marginBottom: 20 }}>
        Print Admission Letter
      </Title>

      <Row gutter={12} style={{ marginBottom: 24 }}>
        <Col>
          <Input
            style={{ width: 320 }}
            placeholder="Enter application number"
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onPressEnter={handleSearch}
            allowClear
          />
        </Col>
        <Col>
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            Search
          </Button>
        </Col>
      </Row>

      {isFetching && <Spin />}

      {!isFetching && isError && (
        <Text type="danger">
          {(error as any)?.response?.data?.message || 'Unable to find applicant. Please try again.'}
        </Text>
      )}

      {!isFetching && !isError && applicationNumber && !applicant && (
        <Text type="secondary">No applicant found for application number "{applicationNumber}".</Text>
      )}

      {!isFetching && applicant && (
        <div className={userStyles.verifyForm}>
          <Row gutter={[16, 12]} className={userStyles.infoGrid}>
            <InfoItem
              label="Applicant Name"
              value={[applicant.firstName, applicant.middleName, applicant.lastName]
                .filter(Boolean)
                .join(' ')}
            />
            <InfoItem label="Application No" value={applicant.applicationNumber} />
            <InfoItem label="Email" value={applicant.email} />
            <InfoItem label="Phone" value={applicant.phoneNumber} />
            <InfoItem label="Program" value={applicant.programName} />
            <InfoItem label="Program Type" value={applicant.programTypeName} />
            <InfoItem label="Mode of Study" value={applicant.modeOfStudyName} />
            <InfoItem label="Application Batch" value={applicant.applicationBatchName} />
            <InfoItem label="Session" value={applicant.sessionName} />
            <InfoItem label="Matric Number" value={applicant.matricNumber} />
            <InfoItem
              label="Date Applied"
              value={applicant.createdDate ? formatFullDate(applicant.createdDate) : undefined}
            />
            <InfoItem
              label="Admission Status"
              value={
                <Tag color={applicant.isAdmitted ? 'success' : 'default'}>
                  {applicant.isAdmitted ? 'Admitted' : 'Not Admitted'}
                </Tag>
              }
            />
          </Row>

          <Row justify="end" style={{ marginTop: 16 }}>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              loading={isPrinting}
              onClick={() => print(applicant.applicationNumber)}
            >
              Print Admission Letter
            </Button>
          </Row>
        </div>
      )}
    </Card>
  );
};

export default AdmissionLetter;
